import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { useClinicianAiSummary } from "../hooks/useClinicianAiSummary";
import { formatDateTime } from "../helper/dateFormatter";
// The generated summary HTML uses .badge/.bg-high|bg-medium|bg-default (priorityBadge.scss)
// and .pill-chip/.pill-chip.is-new (pills.scss) - this project doesn't have a global
// stylesheet entry, each page/component pulls in only what it uses, so these have to be
// imported explicitly here rather than assumed to already be loaded from some other route.
import "../assets/scss/custom/priorityBadge.scss";
import "../assets/scss/custom/pills.scss";
import "../assets/scss/custom/_copdSummary.scss";

interface ClinicianAiSummaryPanelProps {
  patientId: string;
}

// Display-only safety net: the prompt template explicitly forbids wrapping the response in a
// ```html code fence, but LLM output doesn't always comply. The real fix belongs server-side
// (see AI_SUMMARY_CODE_FENCE_HANDOFF.md) - this just keeps a stray fence from rendering as
// literal text in front of clinicians if one slips through before that's deployed.
function stripCodeFence(html: string): string {
  return html.trim().replace(/^```(?:html)?\s*/i, "").replace(/```\s*$/, "");
}

// DOMPurify's default allow-list strips target/rel from anchors (they're excluded by
// default since target="_blank" without rel="noopener noreferrer" enables reverse
// tabnabbing). The citation links this panel renders rely on opening in a new tab, so we
// allow-list those attributes and force the safe rel pairing on any target="_blank" anchor.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const SkeletonRows = () => (
  <div className="copd-summary">
    {Array.from({ length: 4 }).map((_, index) => (
      <span className="placeholder-wave d-block mb-2" key={index}>
        <span className="placeholder w-100"></span>
      </span>
    ))}
  </div>
);

export const ClinicianAiSummaryPanel = ({
  patientId,
}: ClinicianAiSummaryPanelProps) => {
  const { status, summary, errorMessage, regenerate } =
    useClinicianAiSummary(patientId);
  const summaryContainerRef = useRef<HTMLDivElement>(null);

  // "Full Clinical Detail" toggle: the panel content is injected HTML that React doesn't
  // own, so the toggle interaction has to be a delegated listener against a fixed DOM
  // contract (see AI_SUMMARY_HTML_STYLE_HANDOFF.md) rather than React state.
  useEffect(() => {
    const container = summaryContainerRef.current;
    if (!container) return;

    const handleClick = (event: MouseEvent) => {
      const toggle = (event.target as HTMLElement)?.closest(
        ".clinical-detail-toggle"
      );
      if (!toggle || !container.contains(toggle)) return;
      const panel = toggle.nextElementSibling as HTMLElement | null;
      if (!panel || !panel.classList.contains("clinical-detail-panel")) return;
      const isHidden = panel.hasAttribute("hidden");
      if (isHidden) {
        panel.removeAttribute("hidden");
      } else {
        panel.setAttribute("hidden", "");
      }
      toggle.setAttribute("aria-expanded", String(isHidden));
    };

    container.addEventListener("click", handleClick);
    return () => container.removeEventListener("click", handleClick);
  }, [summary]);

  const isBusy = status === "loading" || status === "pending" || status === "processing";

  return (
    <div>
      {(status === "idle" || status === "loading") && <SkeletonRows />}

      {(status === "pending" || status === "processing") && (
        <div className="copd-summary">
          <p className="fs-13 text-muted mb-3">Generating summary&hellip;</p>
          <SkeletonRows />
        </div>
      )}

      {status === "empty" && (
        <div className="copd-summary">
          <p className="fs-13 text-muted mb-3">
            No pre-visit summary has been generated yet.
          </p>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary rounded-pill"
            onClick={regenerate}
          >
            Generate Summary
          </button>
        </div>
      )}

      {status === "error" && (
        <div className="copd-summary">
          <p className="fs-13 text-danger mb-3">
            {errorMessage ?? "Something went wrong generating this summary."}
          </p>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary rounded-pill"
            onClick={regenerate}
          >
            Try again
          </button>
        </div>
      )}

      {status === "completed" && summary?.generatedSummary && (
        <>
          <div
            className="copd-summary"
            ref={summaryContainerRef}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(stripCodeFence(summary.generatedSummary), {
                ADD_ATTR: ["target", "rel"],
              }),
            }}
          />
          <div className="ai-summary-footer d-flex align-items-center justify-content-between">
            <span className="fs-11 text-muted">
              Generated {formatDateTime(summary.completedAt ?? summary.createdAt)}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary rounded-pill fs-11"
              disabled={isBusy}
              onClick={regenerate}
            >
              Regenerate Summary
            </button>
          </div>
        </>
      )}
    </div>
  );
};
